// i18n — zh/en locales, persisted to localStorage.
// Public API: getLang() / setLang() / toggleLang() / useLang() / useT()
// Storage key: design-lang; default 'zh'. Syncs <html lang> (zh-CN / en).
// Lookup fallback: current lang → zh → the key itself. Param interpolation {name}.

import { useSyncExternalStore } from 'react';

export type Lang = 'zh' | 'en';

type Dict = Record<string, string>;
type Dicts = Record<Lang, Dict>;

const STORAGE_KEY = 'design-lang';

const DICT: Dicts = {
  zh: {
    'topbar.export': '导出 PNG',
    'topbar.logout': '登出',
    'cloud.paused': '云同步暂不可用，正在自动重试…',
    'cloud.syncing': '正在同步到云端…',
    'cloud.retrying': '云端暂时不可达，正在自动重试…',
    'cloud.rejected': '云端拒绝保存：项目审查记录不完整（409）。重新打开对应项目完成一次审查后即可恢复同步。',
    'topbar.admin': '管理',
    'topbar.menu': '菜单',
    'admin.title': '用户管理',
    'admin.users': '用户',
    'admin.invites': '邀请码',
    'admin.createInvite': '生成邀请码',
    'admin.copy': '复制',
    'admin.inviteHint': '邀请码一次性有效：注册时填写即直接激活；无码注册需在此「启用」。本次会话生成的邀请码显示在上方（刷新后不再显示）。',
    'admin.role': '角色',
    'admin.status': '状态',
    'admin.lastLogin': '最近登录',
    'admin.never': '从未',
    'admin.you': '你',
    'admin.activate': '启用',
    'admin.disable': '停用',
    'admin.delete': '删除',
    'admin.statusActive': '正常',
    'admin.statusPending': '待审核',
    'admin.statusDisabled': '已停用',
    'admin.opFailed': '操作失败，请重试',
    'topbar.lang.label': 'EN',
    'topbar.lang.title': 'Switch to English',
    'topbar.theme.toLight': '切换到浅色模式',
    'topbar.theme.toDark': '切换到深色模式',

    'project.title': '项目',
    'project.new': '新建设计',
    'project.empty': '还没有项目',
    'project.untitled': '未命名设计',
    'project.delete': '删除',
    'project.rename': '重命名',
    'project.deleteConfirm': '确认删除？',
    'project.cancel': '取消',
    'project.none.title': '没有打开的设计',
    'project.none.hint': '新建一个设计开始',
    'project.backHome': '返回首页',
    'home.newTitle': '新建项目',
    'home.galleryTitle': '全部项目',
    'home.emptyHint': '从上面选一个画布尺寸开始',
    'home.updated': '更新于 {time}',
    'side.regionEdit': '框选编辑',
    'region.hint': '拖拽框选要编辑的区域 · Esc 取消',
    'region.placeholder': '描述要对选中区域做的修改…',
    'region.confirm': '开始编辑',
    'region.cancel': '取消',
    'region.done': '区域编辑完成：',
    'region.tooSmall': '选区太小或过扁：编辑模型要求输入至少 240×240，请框选更大的区域',
    'nav.chat': '对话',
    'nav.canvas': '画布',
    'nav.layers': '图层',
    'preset.poster': '海报',
    'preset.mobile': '手机屏',
    'preset.desktop': '桌面网页',
    'preset.square': '方形',
    'side.canvas': '画布',
    'side.canvas.size': '尺寸',
    'side.canvas.custom': '自定义',
    'side.canvas.bg': '背景色',
    'side.rect.fill': '填充',
    'side.rect.radius': '圆角',
    'side.rect.stroke': '描边',
    'side.icon.color': '图标颜色',
    'side.rect.strokeWidth': '描边宽',
    'side.layer.rect': '矩形',

    'page.bar': '页面',
    'page.add': '新增页面',
    'page.delete': '删除页面',
    'page.renameHint': '双击重命名',
    'topbar.preview': '预览',
    'topbar.exitPreview': '退出预览',
    'topbar.undo': '撤销 (Ctrl+Z)',
    'topbar.redo': '重做 (Ctrl+Shift+Z)',
    'side.linkTo': '跳转到页',
    'side.linkTo.none': '无',
    'side.multi.title': '已选 {n} 个对象',
    'side.group': '成组',
    'side.ungroup': '取消成组',
    'side.deleteSelected': '删除所选',
    'side.grouped': '已成组',
    'side.components': '组件库',
    'side.components.name': '组件名称（可空）',
    'side.components.save': '存所选为组件',
    'side.components.insert': '插入到画布',
    'side.components.delete': '删除组件',
    'side.components.empty': '选中对象后可存为组件，跨项目复用',
    'side.components.count': '{n} 个对象',
    'side.components.defaultName': '组件 {n}',

    'chat.title': '对话',
    'chat.empty': '用一句话开始你的设计：',
    'chat.suggestion.1': '山月咖啡开业海报，深蓝+金色，高级简约风',
    'chat.suggestion.2': '把标题改成「山月咖啡·城市会客厅」',
    'chat.suggestion.3': '背景色调暖一点，加一杯拉花拿铁',
    'chat.placeholder': '描述设计需求…',
    'chat.placeholderBusy': '设计进行中…',
    'chat.send': '发送',
    'chat.attach': '添加图片（也可直接粘贴截图）',
    'chat.attachFail': '上传失败，点击移除',
    'chat.phase.0': '分析需求',
    'chat.phase.1': '构思布局',
    'chat.phase.2': '选择工具',
    'chat.phase.3': '撰写生成指令',
    'chat.retry': '重试',
    // SPEC-414 T-b：异步 turn（提交→轮询；409 单飞行冲突 + 600s 轮询 backstop）
    'chat.turnConflict': '上一轮仍在进行中，请先点停止再发送',
    'chat.turnBackstop': 'Agent 已运行超过 10 分钟，仍在继续。继续等待？\n\n「确定」= 继续等待；「取消」= 停止本轮。',
    'chat.turnRound': '已执行 {n} 轮',
    // SPEC-392 段B：视觉审查闭环（停止按钮 + done 消息）
    'chat.stopLoop': '停止自动审查',
    'chat.loopDone': '生成完毕',
    'chat.loopSoftLeft': '。另有 {n} 条软性建议未处理，可继续提出',
    'chat.loopBudgetLeft': '。已达修正轮数上限，仍有 {n} 处问题未修复',
    'chat.loopUnresolved': '修正轮数上限已用尽，本稿尚未完全收敛：仍有 {n} 处问题未解决（清单见下，可点「继续修正」接着处理）',
    'chat.residualTitle': '残留问题（修正预算已用尽）',
    'chat.continueRefine': '继续修正',
    // SPEC-427 A: frozen-verdict 硬熔断诊断文案
    'chat.frozenTitle': '冻结问题（连续两轮审查结果一致，文字修正无效）',
    'chat.frozenDiag': '。修正熔断：连续 {n} 轮修正后审查结果完全一致，判定文字修正无效，已停止修正循环',
    'chat.frozenAdvice': '建议：重建该区域、上传参考图后重试，或人工处理',
    'chat.frozenRejected': '该修正已被熔断拒绝：连续多轮审查结果一致，重复相同修正无效。建议：重建该区域、上传参考图，或人工处理。',
    'chat.processRegression': '回归',
    'chat.scoresTitle': '维度评分',
    'chat.processTitle': '生成过程 · {n} 步 · {s}s',
    'chat.processStep.screenshot': '截图',
    'chat.processStep.critique': '视觉审查',
    'chat.processStep.diff': '差异审查',
    'chat.processStep.refine': '修正',
    'chat.processStep.plan': '设计方案',
    'chat.plan.intent': '意图解读',
    'chat.plan.zones': '分区示意',
    'chat.plan.objects': '对象计划',
    'chat.plan.bar': '交付门槛（M2 接 VERIFY 勾选）',
    'chat.plan.limits': '已知能力缺口',
    'chat.plan.confirm': '确定',
    'chat.plan.confirmed': '已确定',
    'chat.plan.retry': '重试（换方案）',
    'chat.plan.input': '提修改',
    'chat.plan.inputPlaceholder': '说出要改哪一处，回车发送',
    'chat.plan.loadFailed': '方案详情加载失败',
    'chat.processRound': '第 {n} 轮',
    'chat.processPass': '通过',
    'chat.processFail': '未通过',
    'chat.processHardSoft': '硬伤 {h} · 软性 {s}',
    'chat.processDiffMetrics': '色板差 {d} · 文本命中 {r}%',
    'chat.loopDiffDown': '。差异审查服务暂不可用，本轮未执行修正',
    'chat.processImgExpired': '截图已过期',

    'side.layers': '图层',
    'side.layers.empty': '空画布 —— 在左侧描述你的设计',
    'side.props': '属性',
    'side.props.close': '关闭属性面板',
    'side.props.collapse': '收起属性面板（腾出画布）',
    'side.props.expand': '展开属性面板',
    'panel.collapse.chat': '收起对话栏',
    'panel.expand.chat': '展开对话栏',
    'panel.collapse.layers': '收起图层栏',
    'panel.expand.layers': '展开图层栏',
    'side.alignTo.title': '对齐所选对象',
    'side.alignTo.left': '左对齐',
    'side.alignTo.hcenter': '水平居中',
    'side.alignTo.right': '右对齐',
    'side.alignTo.top': '顶对齐',
    'side.alignTo.vcenter': '垂直居中',
    'side.alignTo.bottom': '底对齐',
    'side.mask': '形状裁剪',
    'side.mask.none': '无',
    'side.mask.radius': '圆角矩形',
    'side.mask.ellipse': '椭圆',
    'side.content': '内容',
    'side.fontSize': '字号',
    'side.fontFamily': '字体',
    'side.fontFamily.default': '默认',
    'side.fontFamily.unsupported': '本机字体需 Chrome/Edge 并授权',
    'side.color': '颜色',
    'side.weight': '字重',
    'side.weight.normal': '常规',
    'side.weight.bold': '粗体',
    'side.align': '对齐',
    'side.align.left': '左',
    'side.align.center': '中',
    'side.align.right': '右',
    'side.retryGen': '重试生成',
    'side.layer.up': '上移一层',
    'side.layer.down': '下移一层',
    'side.layer.delete': '删除',
    'side.layer.hide': '隐藏',
    'side.layer.show': '显示',
    'side.layer.renameHint': '双击重命名',
    'side.layer.renameInput': '重命名图层',
    'side.group.count': '{n} 个成员',
    'side.layer.text': '文本',
    'side.layer.image': '图片',
    'side.layer.frame': '框架',
    'side.layer.icon': '图标',
    'side.layer.collapse': '折叠',
    'side.layer.expand': '展开',

    'canvas.queued': '排队中',
    'canvas.generating': '生成中',
    'canvas.genFailed': '生成失败 · 右侧可重试',
    'canvas.agentCursor': 'Agent',
    'canvas.exportFailed': '导出失败：图片跨域污染了画布（dashscope 临时 URL 的 CORS 限制）。已知限制。',

    'login.subtitle': '登录以继续',
    'login.username': '用户名',
    'login.password': '密码',
    'login.submit': '登录',
    'login.submitting': '登录中…',
    'login.badCredentials': '用户名或密码错误',
    'login.notConfigured': '登录服务未配置，请联系管理员',
    'login.networkError': '网络错误，请重试',
    'login.expired': '会话已过期，请重新登录',
    'login.registerSubtitle': '注册新账号（邀请制）',
    'login.registerSubmit': '注册',
    'login.invite': '邀请码（可选）',
    'login.invitePlaceholder': '有邀请码可直接激活',
    'login.pendingNotice': '注册成功，等待管理员审核后即可登录',
    'login.badUsername': '用户名不合法（2-32位字母/数字/_-）',
    'login.badPassword': '密码至少 8 位',
    'login.userExists': '用户名已存在',
    'login.badInvite': '邀请码无效或已被使用',
    'login.noAccount': '没有账号？注册',
    'login.haveAccount': '已有账号？去登录',
  },
  en: {
    'topbar.export': 'Export PNG',
    'topbar.logout': 'Log out',
    'cloud.paused': 'Cloud sync unavailable — retrying…',
    'cloud.syncing': 'Syncing to cloud…',
    'cloud.retrying': 'Cloud unreachable — retrying…',
    'cloud.rejected': 'Cloud rejected the save: a project has an incomplete review record (409). Reopen that project and finish a review to resume syncing.',
    'topbar.admin': 'Admin',
    'topbar.menu': 'Menu',
    'admin.title': 'User management',
    'admin.users': 'Users',
    'admin.invites': 'Invite codes',
    'admin.createInvite': 'Generate invite code',
    'admin.copy': 'Copy',
    'admin.inviteHint': 'Each code is single-use: entering it at sign-up activates the account immediately. Sign-ups without a code need "Activate" here. Codes generated this session appear above (gone after reload).',
    'admin.role': 'Role',
    'admin.status': 'Status',
    'admin.lastLogin': 'Last login',
    'admin.never': 'never',
    'admin.you': 'you',
    'admin.activate': 'Activate',
    'admin.disable': 'Disable',
    'admin.delete': 'Delete',
    'admin.statusActive': 'active',
    'admin.statusPending': 'pending',
    'admin.statusDisabled': 'disabled',
    'admin.opFailed': 'Operation failed, please retry',
    'topbar.lang.label': '中文',
    'topbar.lang.title': '切换到中文',
    'topbar.theme.toLight': 'Switch to light mode',
    'topbar.theme.toDark': 'Switch to dark mode',

    'project.title': 'Projects',
    'project.new': 'New design',
    'project.empty': 'No projects yet',
    'project.untitled': 'Untitled design',
    'project.delete': 'Delete',
    'project.rename': 'Rename',
    'project.deleteConfirm': 'Delete it?',
    'project.cancel': 'Cancel',
    'project.none.title': 'No design open',
    'project.none.hint': 'Create a new design to start',
    'project.backHome': 'Home',
    'home.newTitle': 'New project',
    'home.galleryTitle': 'All projects',
    'home.emptyHint': 'Pick a canvas size above to start',
    'home.updated': 'Updated {time}',
    'side.regionEdit': 'Edit region',
    'region.hint': 'Drag to select the region to edit · Esc to cancel',
    'region.tooSmall': 'Selection too small or too thin: the edit model needs at least 240×240 input — please select a larger region',
    'region.placeholder': 'Describe the change for the selected region…',
    'region.confirm': 'Edit',
    'region.cancel': 'Cancel',
    'region.done': 'Region edit done:',
    'nav.chat': 'Chat',
    'nav.canvas': 'Canvas',
    'nav.layers': 'Layers',
    'preset.poster': 'Poster',
    'preset.mobile': 'Mobile',
    'preset.desktop': 'Desktop',
    'preset.square': 'Square',
    'side.canvas': 'Canvas',
    'side.canvas.size': 'Size',
    'side.canvas.custom': 'Custom',
    'side.canvas.bg': 'Background',
    'side.rect.fill': 'Fill',
    'side.rect.radius': 'Radius',
    'side.rect.stroke': 'Stroke',
    'side.icon.color': 'Icon color',
    'side.rect.strokeWidth': 'Stroke width',
    'side.layer.rect': 'Rect',

    'page.bar': 'Pages',
    'page.add': 'Add page',
    'page.delete': 'Delete page',
    'page.renameHint': 'Double-click to rename',
    'topbar.preview': 'Preview',
    'topbar.exitPreview': 'Exit preview',
    'topbar.undo': 'Undo (Ctrl+Z)',
    'topbar.redo': 'Redo (Ctrl+Shift+Z)',
    'side.linkTo': 'Link to page',
    'side.linkTo.none': 'None',
    'side.multi.title': '{n} objects selected',
    'side.group': 'Group',
    'side.ungroup': 'Ungroup',
    'side.deleteSelected': 'Delete selected',
    'side.grouped': 'Grouped',
    'side.components': 'Components',
    'side.components.name': 'Component name (optional)',
    'side.components.save': 'Save selection',
    'side.components.insert': 'Insert into canvas',
    'side.components.delete': 'Delete component',
    'side.components.empty': 'Select objects, then save as a reusable component',
    'side.components.count': '{n} objects',
    'side.components.defaultName': 'Component {n}',

    'chat.title': 'Chat',
    'chat.empty': 'Start your design with one sentence:',
    'chat.suggestion.1': 'Grand opening poster for Shan Yue Café, deep blue + gold, minimal premium',
    'chat.suggestion.2': 'Change the headline to "Shan Yue Café · City Lounge"',
    'chat.suggestion.3': 'Warm up the background, add a latte with latte art',
    'chat.placeholder': 'Describe the design…',
    'chat.placeholderBusy': 'Working…',
    'chat.send': 'Send',
    'chat.attach': 'Add image (or paste a screenshot)',
    'chat.attachFail': 'Upload failed — click to remove',
    'chat.phase.0': 'Reading the brief',
    'chat.phase.1': 'Composing the layout',
    'chat.phase.2': 'Picking tools',
    'chat.phase.3': 'Drafting the prompt',
    'chat.retry': 'Retry',
    // SPEC-414 T-b: async turns (submit → poll; 409 single-flight conflict + 600s poll backstop)
    'chat.turnConflict': 'The previous turn is still running — press stop before sending again.',
    'chat.turnBackstop': 'The agent has been running for over 10 minutes and is still going. Keep waiting?\n\nOK = keep waiting; Cancel = stop this turn.',
    'chat.turnRound': 'Rounds done: {n}',
    // SPEC-392 segment B: visual review loop (stop button + done message)
    'chat.stopLoop': 'Stop auto-review',
    'chat.loopDone': 'Done',
    'chat.loopSoftLeft': '. {n} soft suggestions left unaddressed — feel free to follow up',
    'chat.loopBudgetLeft': '. Refine budget exhausted; {n} issues remain unresolved',
    'chat.loopUnresolved': 'Refine budget exhausted — this draft has NOT fully converged: {n} issue(s) remain unresolved (listed below)',
    'chat.residualTitle': 'Remaining issues (refine budget exhausted)',
    'chat.continueRefine': 'Continue refining',
    // SPEC-427 A: frozen-verdict circuit-breaker diagnostics
    'chat.frozenTitle': 'Frozen issues (two identical verdicts in a row — text fixes ineffective)',
    'chat.frozenDiag': '. Refine circuit breaker: verdicts stayed identical across {n} fix rounds — text fixes are ineffective, so the refine loop has been stopped',
    'chat.frozenAdvice': 'Suggestions: rebuild this region, retry with a reference image, or fix it manually',
    'chat.frozenRejected': 'Refine rejected by the circuit breaker: verdicts were identical across rounds, so repeating the same fix will not help. Rebuild this region, attach a reference image, or fix it manually.',
    'chat.processRegression': 'Regression',
    'chat.scoresTitle': 'Dimension scores',
    'chat.processTitle': 'Process · {n} steps · {s}s',
    'chat.processStep.screenshot': 'Screenshot',
    'chat.processStep.critique': 'Visual review',
    'chat.processStep.diff': 'Diff review',
    'chat.processStep.refine': 'Refine',
    'chat.processStep.plan': 'Design plan',
    'chat.plan.intent': 'Intent',
    'chat.plan.zones': 'Zones',
    'chat.plan.objects': 'Object plan',
    'chat.plan.bar': 'Delivery bar (VERIFY checks land in M2)',
    'chat.plan.limits': 'Known capability gaps',
    'chat.plan.confirm': 'Confirm',
    'chat.plan.confirmed': 'Confirmed',
    'chat.plan.retry': 'Retry (new plan)',
    'chat.plan.input': 'Revise',
    'chat.plan.inputPlaceholder': 'Say what to change, Enter to send',
    'chat.plan.loadFailed': 'Failed to load plan details',
    'chat.processRound': 'Round {n}',
    'chat.processPass': 'Passed',
    'chat.processFail': 'Failed',
    'chat.processHardSoft': '{h} hard · {s} soft',
    'chat.processDiffMetrics': 'palette Δ {d} · text match {r}%',
    'chat.loopDiffDown': '. Diff review unavailable — no correction applied this round',
    'chat.processImgExpired': 'Screenshot expired',

    'side.layers': 'Layers',
    'side.layers.empty': 'Empty canvas — describe your design on the left',
    'side.props': 'Properties',
    'side.props.close': 'Close properties',
    'side.props.collapse': 'Collapse panel (clear canvas)',
    'side.props.expand': 'Expand properties',
    'panel.collapse.chat': 'Collapse chat panel',
    'panel.expand.chat': 'Expand chat panel',
    'panel.collapse.layers': 'Collapse layers panel',
    'panel.expand.layers': 'Expand layers panel',
    'side.alignTo.title': 'Align selected objects',
    'side.alignTo.left': 'Align left',
    'side.alignTo.hcenter': 'Align horizontal center',
    'side.alignTo.right': 'Align right',
    'side.alignTo.top': 'Align top',
    'side.alignTo.vcenter': 'Align vertical center',
    'side.alignTo.bottom': 'Align bottom',
    'side.mask': 'Shape mask',
    'side.mask.none': 'None',
    'side.mask.radius': 'Rounded rect',
    'side.mask.ellipse': 'Ellipse',
    'side.content': 'Content',
    'side.fontSize': 'Size',
    'side.fontFamily': 'Font',
    'side.fontFamily.default': 'Default',
    'side.fontFamily.unsupported': 'Local fonts need Chrome/Edge + permission',
    'side.color': 'Color',
    'side.weight': 'Weight',
    'side.weight.normal': 'Regular',
    'side.weight.bold': 'Bold',
    'side.align': 'Align',
    'side.align.left': 'Left',
    'side.align.center': 'Center',
    'side.align.right': 'Right',
    'side.retryGen': 'Retry generation',
    'side.layer.up': 'Move up',
    'side.layer.down': 'Move down',
    'side.layer.delete': 'Delete',
    'side.layer.hide': 'Hide',
    'side.layer.show': 'Show',
    'side.layer.renameHint': 'Double-click to rename',
    'side.layer.renameInput': 'Rename layer',
    'side.group.count': '{n} members',
    'side.layer.text': 'Text',
    'side.layer.image': 'Image',
    'side.layer.frame': 'Frame',
    'side.layer.icon': 'Icon',
    'side.layer.collapse': 'Collapse',
    'side.layer.expand': 'Expand',

    'canvas.queued': 'Queued',
    'canvas.generating': 'Rendering',
    'canvas.genFailed': 'Generation failed · retry from the right panel',
    'canvas.agentCursor': 'Agent',
    'canvas.exportFailed': 'Export failed: cross-origin image tainted the canvas (dashscope temp URL CORS). Known limitation.',

    'login.subtitle': 'Sign in to continue',
    'login.username': 'Username',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.submitting': 'Signing in…',
    'login.badCredentials': 'Invalid username or password',
    'login.notConfigured': 'Sign-in is not configured. Contact the admin.',
    'login.networkError': 'Network error, please retry',
    'login.expired': 'Session expired, please sign in again',
    'login.registerSubtitle': 'Create an account (invite-based)',
    'login.registerSubmit': 'Sign up',
    'login.invite': 'Invite code (optional)',
    'login.invitePlaceholder': 'With a code you start active',
    'login.pendingNotice': 'Registered — you can sign in once an admin approves you',
    'login.badUsername': 'Invalid username (2-32 chars: a-z 0-9 _ -)',
    'login.badPassword': 'Password must be at least 8 characters',
    'login.userExists': 'Username already taken',
    'login.badInvite': 'Invite code invalid or already used',
    'login.noAccount': 'No account? Sign up',
    'login.haveAccount': 'Have an account? Sign in',
  },
};

export function getLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'zh' || v === 'en') return v;
  } catch { /* ignore */ }
  return 'zh';
}

const listeners = new Set<() => void>();
function emit() {
  document.documentElement.lang = getLang() === 'zh' ? 'zh-CN' : 'en';
  listeners.forEach(l => l());
}

export function setLang(l: Lang): void {
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  emit();
}

export function toggleLang(): void {
  setLang(getLang() === 'zh' ? 'en' : 'zh');
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang);
}

export function translate(lang: Lang, key: string, params?: Record<string, unknown>): string {
  let s = DICT[lang][key] ?? DICT.zh[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}

/** useT() — returns t(key, params?) bound to the current language */
export function useT(): (key: string, params?: Record<string, unknown>) => string {
  const lang = useLang();
  return (key, params) => translate(lang, key, params);
}

// Initialize <html lang> (synced at module load, no need to wait for first render)
document.documentElement.lang = getLang() === 'zh' ? 'zh-CN' : 'en';
